import React, { useState, useRef, useEffect } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { appSelector } from "@podcasts/store/application";
import { Button, Input } from "../../";
import styles from "./EmailVerificationModal.module.scss";
import mailIcon from "../../../assets/images/ico/mail.svg";
import closeIcon from "../../../assets/images/ico/close.svg";

interface EmailVerificationModalProps {
  errorMessage?: string;
  isDisabledVerifyButton: boolean;
  onVerify: (code: string) => Promise<any>;
  onResend: () => Promise<any>;
  onClose: () => Promise<void> | void;
}

export function EmailVerificationModal({
  onVerify,
  onResend,
  onClose,
  errorMessage,
  isDisabledVerifyButton,
}: EmailVerificationModalProps) {
  const [code, setCode] = useState(["", "", "", ""]);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const { user } = useSelector(appSelector) as any;

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (user?.next_resend_verification_at) {
      const nextResendTime = new Date(
        user.next_resend_verification_at,
      ).getTime();
      const currentTime = Date.now();
      const secondsUntilNextResend = Math.max(
        0,
        Math.ceil((nextResendTime - currentTime) / 1000),
      );
      setCountdown(secondsUntilNextResend);
    }
  }, [user?.next_resend_verification_at]);

  const handleChange = (index: number, value: string) => {
    // Only accept numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take the last character
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 4; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 3);
    inputRefs[nextIndex].current?.focus();
  };

  const handleVerify = () => {
    const verificationCode = code.join("");
    if (verificationCode.length === 4) {
      onVerify(verificationCode);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || isResending) return;

    setIsResending(true);
    try {
      await onResend();
    } catch (error) {
      console.error("Error resending verification code:", error);
    }
    setIsResending(false);
  };

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      closeTimeoutMS={200}
      className={styles.modal}
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <img
        src={closeIcon}
        alt="Close"
        className={styles["close-icon"]}
        onClick={onClose}
      />
      <div className={styles.content}>
        <div className={styles["icon-container"]}>
          <img src={mailIcon} alt="Email" className={styles["mail-icon"]} />
        </div>
        <p className={styles.description}>
          A verification code has been sent to your email. Please check your
          inbox or spam folder. The code is valid for 5 minutes.
          <br />
          Please enter the code below:
        </p>

        <div className={styles["code-inputs"]}>
          {code.map((digit, index) => (
            <div key={index} className={styles["code-input-wrapper"]}>
              <Input
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                name={`code-${index}`}
                value={digit}
                onChange={(e: any) => handleChange(index, e.target.value)}
                onKeyDown={(e: any) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onBlur={() => {}}
              />
            </div>
          ))}
        </div>

        {errorMessage && (
          <div className={styles["error-message"]}>{errorMessage}</div>
        )}

        <div className={styles["resend-container"]}>
          <p>Didn’t receive a code?</p>&nbsp;
          <button
            type="button"
            className={`${styles["resend-text"]} ${
              countdown > 0 ? styles["disabled"] : ""
            }`}
            onClick={handleResend}
            disabled={countdown > 0 || isResending}
            style={{
              cursor: countdown > 0 || isResending ? "not-allowed" : "pointer",
              opacity: countdown > 0 || isResending ? 0.5 : 1,
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
            }}
          >
            {countdown > 0
              ? `Resend (${countdown}s)`
              : isResending
                ? "Sending..."
                : "Resend"}
          </button>
        </div>

        <div className={styles["button-container"]}>
          <Button
            name="Verify"
            onClick={handleVerify}
            disabled={isDisabledVerifyButton}
          />
        </div>
        <div className={styles["info-container"]}>
          <p className="d-inline">
            If you are experiencing difficulties, please reach out to
          </p>
          &nbsp;
          <a href="mailto:info@podcastfellowship.org">
            info@podcastfellowship.org
          </a>
        </div>
      </div>
    </Modal>
  );
}

export default EmailVerificationModal;
